/**
 * Instance Admin → Marketplace: Twasta's researched defaults with this
 * instance's overrides (server sizes and hosting markup, review times, trial
 * rules, retention, fees), and the listing review queue.
 * Defaults: backend/app/data/marketplace/defaults.json · doc: docs/app_marketplace.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  adminScreenshotUrl, getAdminListings, getAdminPolicy, money, previewAdminPolicy, reviewListing, saveAdminPolicy,
  takedownListing, type AdminPolicy, type Listing,
} from '@/api/marketplace'
import { errText, InfoBox, Section, Td, Th, chipStyle, tableStyle } from './adminUi'
import BuildTermsPanel from './BuildTermsPanel'
import BuildOrdersPanel from './BuildOrdersPanel'

type Over = Record<string, any>

const SECTIONS: { key: string; title: string; why: string; fields: [string, string, string][] }[] = [
  {
    key: 'review', title: 'Listing review time',
    why: 'Other marketplaces take 5–10 business days just to start (Atlassian), up to 10 for a first look (HubSpot) and 2+ weeks in practice (Shopify). A Twasta app has already passed its go-live checks and runs on our hosting, so 5 business days is a promise we can keep.',
    fields: [['first_review_business_days', 'First review of a new app', 'business days'], ['update_review_business_days', 'Review of a listing change', 'business days'], ['escalate_after_business_days', 'Escalate to a lead after', 'business days']],
  },
  {
    key: 'trial', title: 'Free-trial rules',
    why: 'Trials are funded by the publisher’s prepaid trial budget. A trial sets aside its full cost when it starts, uses what the days actually cost, and returns the rest. When the budget can’t cover one more trial, new trials stop; running ones finish.',
    fields: [['max_days', 'Longest trial a publisher can offer', 'days'], ['max_people', 'Most people in one trial', 'people'], ['min_topup_trials', 'Smallest budget top-up', 'trials'], ['low_budget_trials', 'Warn the publisher when the budget covers', 'trials or fewer'], ['reminder_days_before_end', 'Remind the customer before the trial ends', 'days']],
  },
  {
    key: 'retention', title: 'Customer data after a subscription ends',
    why: '30 days of read-only export matches the common floor (Zoho Mail / FSM, Salesforce’s return window); deletion at 90 days matches Microsoft 365’s earliest point and sits inside Zoho Books’ 120; backup copies gone within 30 more. India’s DPDP rules (from May 2027) require erasure once the purpose is over and 1 year of processing logs; invoices are kept 8 years under Indian accounting and GST rules.',
    fields: [['readonly_days', 'Read-only access and export after it ends', 'days'], ['delete_after_days', 'Permanently delete data after', 'days'], ['backup_purge_days', 'Backup copies purged within a further', 'days'], ['trial_data_days', 'Delete data of a trial that never became paid after', 'days'], ['invoice_years', 'Keep invoices and payment records', 'years'], ['log_years', 'Keep activity and processing logs', 'years']],
  },
  {
    key: 'billing', title: 'Customer billing',
    why: 'A missed payment keeps the app working through the grace period, with reminders, before the subscription ends.',
    fields: [['grace_days', 'Grace period after a failed payment', 'days'], ['low_balance_warning_days', 'Warn about a low wallet before a charge', 'days']],
  },
]

function deepClone<T>(v: T): T { return JSON.parse(JSON.stringify(v ?? {})) }

/** The overrides this tab edits — the Get-it-built payment terms (`build`)
 *  have their own tab and are carried through a save untouched. */
function withoutBuild(o: Over): Over { const { build: _build, ...rest } = o || {}; return deepClone(rest) }

export default function MarketplaceAdminPanel() {
  const [tab, setTab] = useState<'defaults' | 'build' | 'build_orders' | 'reviews' | 'listings'>('defaults')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['defaults', 'build', 'build_orders', 'reviews', 'listings'] as const).map((t) => (
          <button key={t} type="button" className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t)}>
            {t === 'defaults' ? 'Defaults & overrides' : t === 'build' ? 'Get it built — payment terms' : t === 'build_orders' ? 'Get it built — orders & payouts' : t === 'reviews' ? 'Review queue' : 'All listings'}
          </button>
        ))}
      </div>
      {tab === 'defaults' && <DefaultsPanel />}
      {tab === 'build' && <BuildTermsPanel />}
      {tab === 'build_orders' && <BuildOrdersPanel />}
      {tab === 'reviews' && <ListingsPanel status="in_review" />}
      {tab === 'listings' && <ListingsPanel />}
    </div>
  )
}

function DefaultsPanel() {
  const [policy, setPolicy] = useState<AdminPolicy | null>(null)
  const [over, setOver] = useState<Over>({})
  const [fee, setFee] = useState<number | null>(null)
  const [notice, setNotice] = useState<number | null>(null)
  const [preview, setPreview] = useState<AdminPolicy | null>(null)
  const [previewErr, setPreviewErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR')

  const load = useCallback(() => {
    getAdminPolicy().then((p) => {
      setPolicy(p); setOver(withoutBuild(p.overrides)); setPreview(p)
      setFee(p.effective.fees.marketplace_fee_percent); setNotice(p.effective.fees.price_change_notice_days)
    }).catch((e) => toast.error(errText(e, 'Could not load marketplace settings')))
  }, [])
  useEffect(load, [load])

  // Live preview of the hosting costs and listing impact.
  useEffect(() => {
    if (!policy) return
    const t = window.setTimeout(() => {
      previewAdminPolicy(over).then((p) => { setPreview(p); setPreviewErr(null) }).catch((e) => setPreviewErr(errText(e, 'Invalid value')))
    }, 350)
    return () => window.clearTimeout(t)
  }, [over, policy])

  const d = policy?.defaults
  const changedCount = useMemo(() => countLeaves(over), [over])
  if (!policy || !d) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const setPath = (path: string[], value: unknown) => {
    const next = deepClone(over)
    let cur: Over = next
    for (const k of path.slice(0, -1)) { cur[k] = cur[k] ?? {}; cur = cur[k] }
    if (value === undefined) delete cur[path[path.length - 1]]
    else cur[path[path.length - 1]] = value
    setOver(prune(next))
  }
  const get = (path: string[]): unknown => path.reduce<any>((o, k) => (o == null ? undefined : o[k]), over)
  const getDefault = (path: string[]): unknown => path.reduce<any>((o, k) => (o == null ? undefined : o[k]), d)

  const save = async () => {
    setSaving(true)
    try {
      const keep = policy?.overrides?.build ? { build: policy.overrides.build } : {}
      const p = await saveAdminPolicy({ overrides: { ...over, ...keep }, marketplace_fee_percent: fee ?? undefined, price_change_notice_days: notice ?? undefined })
      setPolicy(p); setOver(withoutBuild(p.overrides)); setPreview(p)
      toast.success('Marketplace settings saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save'))
    } finally {
      setSaving(false)
    }
  }

  const sizes = preview?.sizes[currency] || []
  const enabled = (get(['enabled']) ?? d.enabled) as boolean

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ maxWidth: 720, fontSize: 14, color: 'var(--color-text-secondary)' }}>
          Twasta ships researched defaults for every value below. Change any of them for this instance; a changed value is marked and can be reset. Hosting cost changes reach existing customers only after the price-change notice period.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={chipStyle}>{changedCount ? `${changedCount} changed from Twasta defaults` : 'Using Twasta defaults'}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOver({})}>Reset all</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving || !!previewErr}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
      {previewErr && <InfoBox><span style={{ color: 'var(--color-error)' }}>{previewErr}</span></InfoBox>}

      <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setPath(['enabled'], e.target.checked === d.enabled ? undefined : e.target.checked)} />
        <span><strong>Marketplace is on</strong> — anyone can browse; Pro-and-above project managers can publish.</span>
      </label>

      <Section title="Server sizes and hosting cost">
        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          What a publisher pays Twasta each month for one customer on a server plan: the server cost, plus management overhead, plus Twasta's margin. Publishers must price above it. Server plans are resource tiers on shared hosting — each customer still gets its own private database.
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <PctField label="Management overhead" help="Backups, monitoring, patching, upgrades, support, spare capacity." path={['hosting', 'management_overhead_percent']} get={get} getDefault={getDefault} setPath={setPath} />
          <PctField label="Twasta profit margin" help="Added on top of server cost plus management." path={['hosting', 'profit_margin_percent']} get={get} getDefault={getDefault} setPath={setPath} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            {(['INR', 'USD'] as const).map((c) => <button key={c} type="button" className={`btn btn-sm ${currency === c ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCurrency(c)}>{c}</button>)}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tableStyle, minWidth: 980 }}>
            <thead><tr><Th>Size</Th><Th>Machines and limits</Th><Th>Server cost / month</Th><Th right>+ Management</Th><Th right>+ Margin</Th><Th right>= Hosting cost</Th></tr></thead>
            <tbody>
              {sizes.map((s) => {
                const path = ['server_sizes', s.id, 'server_cost_minor', currency]
                const defMinor = (d.server_sizes.find((x: any) => x.id === s.id)?.server_cost_minor || {})[currency] as number
                const val = (get(path) as number | undefined) ?? defMinor
                return (
                  <tr key={s.id}>
                    <Td><strong>{s.name}</strong><br /><span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Up to {s.max_people} people</span></Td>
                    <Td><span style={{ fontSize: 13, lineHeight: 1.6 }}>App {s.machines.app} · Database {s.machines.database}<br />{s.storage_gb} GB records · {s.files_gb} GB files<br />{Object.entries(s.limits).map(([k, v]) => `${k.split('.').pop()} ${v}`).join(' · ')}</span></Td>
                    <Td>
                      <input type="number" min={0} value={val / 100} style={{ width: 130 }} aria-label={`${s.name} server cost ${currency}`}
                        onChange={(e) => { const minor = Math.round(Number(e.target.value) * 100); setPath(path, minor === defMinor ? undefined : minor) }} />
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        Twasta default {money(defMinor, currency)}{val !== defMinor && <> · <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={() => setPath(path, undefined)}>Reset</button></>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Market: {Object.values(s.market_reference || {}).join(' · ')}</div>
                    </Td>
                    <Td right>{money(s.management_minor, currency)}</Td>
                    <Td right>{money(s.margin_minor, currency)}</Td>
                    <Td right><strong>{money(s.hosting_cost_minor, currency)}</strong></Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {preview && preview.impact.length > 0 && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--color-error-weak)', fontSize: 14 }}>
            <strong>{preview.impact.length} listed price(s) would be at or below the new hosting cost:</strong>{' '}
            {preview.impact.map((i) => `${i.listing} ${i.size === 'person' ? 'per person' : i.size} ${money(i.price_minor, i.currency)} (cost ${money(i.cost_minor, i.currency)})`).join(', ')}. Publishers must reprice before customers can choose those plans.
          </div>
        )}
        <InfoBox>
          <strong>Why these defaults:</strong> server cost sits between Oracle Cloud / Indian-cloud and DigitalOcean-class list prices for the same machines (October 2026, ₹88 per US dollar). Management (30%) pays for backups, monitoring, patching, upgrades, support and spare capacity so servers run at about 70% load; the margin (20%) is Twasta's profit. Hosting cost = server × (1 + management) × (1 + margin), rounded up. An instance on AWS on-demand pays about twice the server cost — raise the server values here. Per-person hosting comes from each plan's rate card (Billing → Cloud cost), which is already a price.
        </InfoBox>
      </Section>

      {SECTIONS.map((sec) => (
        <Section key={sec.key} title={sec.title}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '10px 28px', marginBottom: 10 }}>
            {sec.fields.map(([k, label, unit]) => (
              <NumField key={k} label={label} unit={unit} path={[sec.key, k]} get={get} getDefault={getDefault} setPath={setPath} />
            ))}
          </div>
          <InfoBox><strong>Why:</strong> {sec.why}</InfoBox>
        </Section>
      ))}

      <Section title="Fees and notices">
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 14 }}>Marketplace fee on what customers pay (%)<br />
            <input type="number" min={0} max={100} step="0.5" value={fee ?? ''} onChange={(e) => setFee(Number(e.target.value))} style={{ width: 110, marginTop: 4 }} />
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginLeft: 8 }}>Twasta default 15</span></label>
          <label style={{ fontSize: 14 }}>Notice before a price change binds existing customers (days)<br />
            <input type="number" min={0} max={365} value={notice ?? ''} onChange={(e) => setNotice(Number(e.target.value))} style={{ width: 110, marginTop: 4 }} />
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginLeft: 8 }}>Twasta default 30</span></label>
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>These two are shared with the owner price sheets (Billing → Cloud cost). Who may publish: project managers on Pro or above.</div>
      </Section>
    </div>
  )
}

function NumField({ label, unit, path, get, getDefault, setPath }: { label: string; unit: string; path: string[]; get: (p: string[]) => unknown; getDefault: (p: string[]) => unknown; setPath: (p: string[], v: unknown) => void }) {
  const def = getDefault(path) as number
  const cur = (get(path) as number | undefined)
  const val = cur ?? def
  return (
    <label style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', fontSize: 14, borderBottom: '1px solid var(--color-border)', padding: '8px 0' }}>
      <span>{label}<br /><span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Twasta default {def} {unit}{cur !== undefined && cur !== def && <strong style={{ color: 'var(--color-warning)' }}> · Changed</strong>}</span></span>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="number" min={0} value={val} style={{ width: 80 }} onChange={(e) => { const n = Number(e.target.value); setPath(path, n === def ? undefined : n) }} />
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', minWidth: 70 }}>{unit}</span>
      </span>
    </label>
  )
}

function PctField({ label, help, ...rest }: { label: string; help: string; path: string[]; get: (p: string[]) => unknown; getDefault: (p: string[]) => unknown; setPath: (p: string[], v: unknown) => void }) {
  return (
    <div style={{ minWidth: 300, flex: '1 1 300px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 12px' }}>
      <NumField label={label} unit="%" {...rest} />
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '4px 0 6px' }}>{help}</div>
    </div>
  )
}

function prune(o: Over): Over {
  for (const k of Object.keys(o)) {
    if (o[k] && typeof o[k] === 'object') {
      prune(o[k])
      if (!Object.keys(o[k]).length) delete o[k]
    }
  }
  return o
}

function countLeaves(o: Over): number {
  return Object.values(o || {}).reduce<number>((n, v) => n + (v && typeof v === 'object' ? countLeaves(v) : 1), 0)
}

function ListingsPanel({ status }: { status?: string }) {
  const [rows, setRows] = useState<Listing[] | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const load = useCallback(() => {
    getAdminListings(status).then((r) => setRows(r.listings)).catch((e) => toast.error(errText(e, 'Could not load listings')))
  }, [status])
  useEffect(load, [load])

  const decide = async (l: Listing, approve: boolean) => {
    if (!approve && !note.trim()) { toast.error('Tell the publisher what to change'); return }
    try { await reviewListing(l.id, approve, note); toast.success(approve ? 'Approved — the app is live' : 'Sent back to the publisher'); setNote(''); setOpen(null); load() } catch (e) { toast.error(errText(e, 'Could not save the review')) }
  }
  const takedown = async (l: Listing) => {
    if (!window.confirm(`Remove ${l.name} from the marketplace? Existing customers keep their subscriptions.`)) return
    try { await takedownListing(l.id); load() } catch (e) { toast.error(errText(e, 'Could not remove')) }
  }

  if (!rows) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>
  if (!rows.length) return <InfoBox>{status === 'in_review' ? 'Nothing is waiting for review.' : 'No listings yet.'}</InfoBox>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((l) => (
        <div key={l.id} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 14, background: 'var(--color-bg-secondary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <strong>{l.name}</strong> <span style={chipStyle}>{l.status.replace('_', ' ')}</span>{l.overdue && <span style={{ ...chipStyle, color: 'var(--color-error)', marginLeft: 6 }}>Overdue</span>}
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>by {l.publisher} · project {l.project} · {l.status === 'in_review' && l.review_due_at ? `due ${new Date(l.review_due_at).toLocaleDateString()}` : `${l.installs} installs`}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(open === l.id ? null : l.id)}>{open === l.id ? 'Close' : 'Open'}</button>
              {l.status !== 'removed' && <button type="button" className="btn btn-danger btn-sm" onClick={() => takedown(l)}>Take down</button>}
            </div>
          </div>
          {open === l.id && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
              <div><strong>{l.tagline}</strong> <span style={chipStyle}>{l.category}</span></div>
              <div style={{ whiteSpace: 'pre-line', color: 'var(--color-text-secondary)' }}>{l.description}</div>
              <div>Support: {l.support_email}</div>
              <table style={tableStyle}>
                <thead><tr><Th>Plan</Th><Th right>Price</Th><Th right>Hosting cost</Th><Th right>Publisher earns</Th></tr></thead>
                <tbody>
                  {l.evaluation.person && <tr><Td>Per person</Td><Td right>{money(l.evaluation.person.price_minor, l.currency)}</Td><Td right>{money(l.evaluation.person.cost_minor, l.currency)}</Td><Td right>{money(l.evaluation.person.earn_minor, l.currency)}</Td></tr>}
                  {Object.entries(l.evaluation.server).map(([sid, j]) => <tr key={sid}><Td>{sid} server</Td><Td right>{money(j.price_minor, l.currency)}</Td><Td right>{money(j.cost_minor, l.currency)}</Td><Td right>{money(j.earn_minor, l.currency)}</Td></tr>)}
                </tbody>
              </table>
              <div>Free trial: {l.pricing.trial.days ? `${l.pricing.trial.days} days, up to ${l.pricing.trial.people} people · budget ${money(l.trial_budget.available_minor, l.currency)}` : 'none'}</div>
              {l.screenshots.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {l.screenshots.map((s) => <img key={s.id} src={adminScreenshotUrl(l.id, s.id)} alt={s.caption || 'Screenshot'} style={{ width: 200, borderRadius: 6, border: '1px solid var(--color-border)' }} />)}
                </div>
              )}
              {l.evaluation.errors.length > 0 && <InfoBox><span style={{ color: 'var(--color-error)' }}>{l.evaluation.errors.join(' ')}</span></InfoBox>}
              {l.status === 'in_review' && (
                <>
                  <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the publisher (required when asking for changes)" style={{ font: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => decide(l, true)}>Approve and publish</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => decide(l, false)}>Ask for changes</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
